import React from "react";
import "./NortonBanner.css";
import { useLocalContext } from "../../context/LocalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faShieldHalved,
  faCircleCheck,
  faLaptop,
  faMobileScreen,
  faUsers,
  faGlobe,
  faCloudArrowUp,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

const NortonBanner = () => {
  const { webinfo } = useLocalContext();
  const brand = webinfo?.name || "Our Store";
  const navigate = useNavigate();

  const plans = [
    {
      name: "Norton AntiVirus Plus",
      tag: "Basic cover",
      devices: "1 device",
      vpn: "No VPN",
      storage: "2 GB backup (Windows)",
      bestFor: "Single laptop/desktop – core malware protection",
      route: "/norton/norton-security-plus",
      highlight: "Most affordable for 1 PC/Mac",
    },
    {
      name: "Norton 360 Standard",
      tag: "Popular",
      devices: "1 device",
      vpn: "VPN included",
      storage: "10 GB backup (Windows)",
      bestFor: "Laptop + privacy (VPN) for everyday browsing",
      route: "/norton/norton-360-standard",
      highlight: "Best if you want VPN",
    },
    {
      name: "Norton 360 Deluxe",
      tag: "Best value",
      devices: "Up to 5 devices",
      vpn: "VPN included",
      storage: "50 GB backup (Windows)",
      bestFor: "Family & multi-device protection",
      route: "/norton/norton-360-deluxe",
      highlight: "Ideal for 3–5 devices",
    },
  ];

  const scenarios = [
    {
      icon: faLaptop,
      label: "One laptop / one PC",
      text: "Basic protection for browsing, email, and online payments.",
      match: "Norton AntiVirus Plus",
    },
    {
      icon: faMobileScreen,
      label: "Laptop + phone",
      text: "Want antivirus + VPN for safer Wi-Fi and privacy.",
      match: "Norton 360 Standard",
    },
    {
      icon: faUsers,
      label: "Family devices",
      text: "Multiple phones + laptops under one plan.",
      match: "Norton 360 Deluxe",
    },
  ];

  const goPlan = (planName) => {
    const found = plans.find((p) => p.name === planName);
    if (found?.route) navigate(found.route);
  };

  return (
    <section className="nb2" aria-labelledby="nb2-title">
      <div className="nb2-wrap">
        {/* HERO */}
        <header className="nb2-hero">
          <div className="nb2-copy">
            <span className="nb2-kicker">
              <FontAwesomeIcon icon={faShieldHalved} />
              Protection plan advisor
            </span>

            <h2 id="nb2-title" className="nb2-title">
              Pick the right Protection plan in{" "}
              <span className="nb2-accent">30 seconds</span>
            </h2>

            <p className="nb2-sub">
              Choose based on your <strong>device count</strong> and whether you
              need a <strong>VPN</strong> or <strong>multi-device</strong>{" "}
              coverage. All products here are{" "}
              <strong>digital licenses</strong> with email delivery + activation help.
            </p>

            <div className="nb2-usps" role="list">
              {[
                "Email delivery + activation guide",
                "Clear VPN + device-limit explanation",
                "Backup notes for Windows editions",
              ].map((t) => (
                <div className="nb2-usp" role="listitem" key={t}>
                  <FontAwesomeIcon icon={faCircleCheck} />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          <aside className="nb2-support" aria-label="Call support">
            <div className="nb2-supportCard">
              <div className="nb2-supportTop">
                <div className="nb2-supportTitle">Need a quick recommendation?</div>
                <div className="nb2-supportText">
                  Tell us devices + OS. We’ll suggest the best-fit plan (no upsell).
                </div>
              </div>

              {webinfo?.phonecall ? (
                <a className="nb2-call" href={`tel:${webinfo.phonecall}`}>
                  <FontAwesomeIcon icon={faPhone} />
                  {webinfo?.phone ? `Call ${webinfo.phone}` : "Call now"}
                </a>
              ) : (
                <div className="nb2-call disabled" aria-disabled="true">
                  <FontAwesomeIcon icon={faPhone} />
                  Support available
                </div>
              )}

              <div className="nb2-fine">
                {brand} provides plan guidance + activation help.
              </div>
            </div>
          </aside>
        </header>

        {/* QUICK STEPS */}
        <div className="nb2-steps" aria-hidden="true">
          {[
            { n: "1", t: "Choose your setup", d: "Single, duo, or family devices." },
            { n: "2", t: "Compare plans", d: "Devices, VPN, and backup notes." },
            { n: "3", t: "Buy & activate", d: "Digital key + simple steps." },
          ].map((s) => (
            <div className="nb2-step" key={s.n}>
              <span className="nb2-stepNum">{s.n}</span>
              <div>
                <div className="nb2-stepTitle">{s.t}</div>
                <div className="nb2-stepText">{s.d}</div>
              </div>
            </div>
          ))}
        </div>

        {/* GRID */}
        <div className="nb2-grid">
          {/* SCENARIOS */}
          <div className="nb2-card" aria-label="Pick by scenario">
            <div className="nb2-cardHead">
              <h3>Start with your devices</h3>
              <p>
                Tap the scenario closest to your setup. You’ll land on the plan page.
              </p>
            </div>

            <div className="nb2-scenarios" role="list">
              {scenarios.map((s) => (
                <button
                  key={s.label}
                  className="nb2-scenario"
                  type="button"
                  onClick={() => goPlan(s.match)}
                >
                  <div className="nb2-scenTop">
                    <span className="nb2-scenIcon" aria-hidden="true">
                      <FontAwesomeIcon icon={s.icon} />
                    </span>
                    <div>
                      <div className="nb2-scenLabel">{s.label}</div>
                      <div className="nb2-scenText">{s.text}</div>
                    </div>
                  </div>

                  <div className="nb2-scenPick">
                    Suggested: <strong>{s.match}</strong>
                    <span className="nb2-arrow" aria-hidden="true">
                      <FontAwesomeIcon icon={faBolt} />
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="nb2-notes" role="list">
              <div className="nb2-note" role="listitem">
                <FontAwesomeIcon icon={faGlobe} /> VPN availability can vary by region.
              </div>
              <div className="nb2-note" role="listitem">
                <FontAwesomeIcon icon={faCloudArrowUp} /> Backup applies to Windows editions as listed.
              </div>
            </div>
          </div>

          {/* COMPARISON */}
          <div className="nb2-card" aria-label="Plan comparison">
            <div className="nb2-cardHead">
              <h3>Norton plans comparison</h3>
              <p>Quick overview — open any plan for full details & checkout.</p>
            </div>

            <div className="nb2-table" role="table">
              <div className="nb2-row head" role="row">
                <div className="nb2-cell" role="columnheader">Plan</div>
                <div className="nb2-cell" role="columnheader">Devices</div>
                <div className="nb2-cell" role="columnheader">VPN</div>
                <div className="nb2-cell" role="columnheader">Backup</div>
                <div className="nb2-cell" role="columnheader">Best for</div>
                <div className="nb2-cell" role="columnheader"></div>
              </div>

              {plans.map((p) => (
                <div className="nb2-row" key={p.name} role="row">
                  <div className="nb2-cell plan" role="cell">
                    <div className="nb2-planName">{p.name}</div>
                    <div className="nb2-planMeta">
                      <span className="nb2-tag">{p.tag}</span>
                      <span className="nb2-hint">{p.highlight}</span>
                    </div>
                  </div>
                  <div className="nb2-cell" role="cell">{p.devices}</div>
                  <div className="nb2-cell" role="cell">{p.vpn}</div>
                  <div className="nb2-cell" role="cell">{p.storage}</div>
                  <div className="nb2-cell" role="cell">{p.bestFor}</div>
                  <div className="nb2-cell action" role="cell">
                    <button
                      className="nb2-link"
                      type="button"
                      onClick={() => navigate(p.route)}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="nb2-mini">
              <FontAwesomeIcon icon={faCircleCheck} />
              Features can vary by edition/region — check the plan page before checkout.
            </div>
          </div>
        </div>

        {/* DISCLAIMER */}
        <p className="nb2-disclaimer">
          {brand} provides plan guidance and activation assistance for customers purchasing
          through our store. Brand names and logos belong to their respective owners and
          are used for identification only.
        </p>
      </div>
    </section>
  );
};

export default NortonBanner;
